<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import { RequestStatus } from '@sancare/typescript-commons/src/store/types'
import _ from 'lodash'
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { GroupedWildcardChildItem, JustificationGroupItem, SuggestionJustification } from '@/misc/types'
import { CodingSelection, Suggestion, SuggestionDiagnosis, SuggestionSearch } from '@/stay-displayer/types'
import { StandardModeDiagnosisGroup } from '@/store/modules/stay/mco/types'
import { StayHealthData } from '@/store/modules/stay/types'

import SuggestedChronicDiagnosisCard from './SuggestedChronicDiagnosisCard.vue'
import SuggestedDiagnosisCard from './SuggestedDiagnosisCard.vue'

type CardDiagnosis = SuggestionDiagnosis & { wildcard: string | null }

type JustificationGroup = {
  justification: SuggestionJustification
  diagnoses: CardDiagnosis[]
  wildcards: { reference: string, level: Suggestion['level'] }[]
  staySavedSearches: SuggestionSearch[]
  searchNames: string[]
  searchIds: number[]
}

type ChronicJustificationGroup = {
  stayList: { id: number, rssId: string, stayEnd: string, stayDuration: number }[]
  diagList: CardDiagnosis[]
}

const props = defineProps<{
  appSettings: Record<string, unknown>
  suggestedDiagnoses: Suggestion[]
  rumId: number
  standardModeDiagnosisGroup: StandardModeDiagnosisGroup
  healthData: StayHealthData
  selection?: CodingSelection | null
  isLoading?: boolean
  mode: string
  staySavedSearches?: unknown[] | null
  displayedRumIdx: number
  type?: string | null
  readonly: boolean
}>()

const emit = defineEmits<{
  (e: 'switch-display-mode'): void
  (e: 'make-selection', selection: CodingSelection): void
}>()

const store = useStore()

const showMore = ref(false)

const pmsiCountry = computed(() => store.state.settings.pmsi.country)
const groupedSuggestionJustifications = computed<JustificationGroupItem[] | null>(() => store.state.stayDisplayer.groupedSuggestionJustifications)
const getGroupedSuggestionJustificationsRequest = computed<RequestStatus>(() => store.state.stayDisplayer.getGroupedSuggestionJustificationsRequest)

const orderedSuggestedDiagnoses = computed<Suggestion[]>(() => {
  // Builds new objects rather than mutating the suggestedDiagnoses prop in place: reactively
  // writing into a prop from within the computed that reads it recurses indefinitely.
  const orderedSuggestions = _.map(props.suggestedDiagnoses, (suggestion) => ({
    ...suggestion,
    diagnoses: _.orderBy(suggestion.diagnoses, ['excluded', 'level', 'prediction.confidence'], ['asc', 'desc', 'desc']),
  }))

  return _.orderBy(orderedSuggestions, ['isExcluded', 'level.max', 'confidence.max'], ['asc', 'desc', 'desc'])
})

const displayedSuggestedDiagnoses = computed<Suggestion[]>(() => {
  return _.filter(orderedSuggestedDiagnoses.value, (suggestion) => suggestion.isAlwaysVisible || showMore.value)
})

// Non-wildcard suggestions, keyed by reference, for resolving a backend-provided group's
// suggestion references back into the full diagnosis object (level, description, predictions...)
// that the grouped-by-justifications route itself doesn't carry.
const diagnosisByReference = computed<Record<string, CardDiagnosis>>(() => {
  const map: Record<string, CardDiagnosis> = {}
  for (const suggestion of displayedSuggestedDiagnoses.value) {
    if (!suggestion.isWildcard && suggestion.diagnoses[0]) {
      map[suggestion.reference] = { ...suggestion.diagnoses[0], wildcard: null }
    }
  }
  return map
})

// Chronic DAS cards are always shown regardless of the "show more" toggle (see chronicSuggestions
// below), so their diagnosis metadata must be resolved from every suggestion — not just the
// currently displayed ones, unlike diagnosisByReference above.
const chronicDiagnosisByReference = computed<Record<string, CardDiagnosis>>(() => {
  const map: Record<string, CardDiagnosis> = {}
  for (const suggestion of orderedSuggestedDiagnoses.value) {
    if (!suggestion.isWildcard && suggestion.diagnoses[0]) {
      map[suggestion.reference] = { ...suggestion.diagnoses[0], wildcard: null }
    }
  }
  return map
})

const wildcardSuggestionByReference = computed<Record<string, Suggestion>>(() => {
  const map: Record<string, Suggestion> = {}
  for (const suggestion of displayedSuggestedDiagnoses.value) {
    if (suggestion.isWildcard) {
      map[suggestion.reference] = suggestion
    }
  }
  return map
})

// The parent bucket for a wildcard always lists ALL its children (see backend
// ListStaySuggestionsGroupedByJustificationsPayload::groupParentSuggestion), while a child with its
// own distinct justification additionally gets its own single-child bucket. Tracking those refs lets
// justificationGroups drop them from the generic bucket, so a given child never renders under two
// separate wildcard tiles with two different (one of them wrong) justifications.
const overriddenWildcardChildRefs = computed<Set<string>>(() => {
  const refs = new Set<string>()
  for (const group of groupedSuggestionJustifications.value ?? []) {
    for (const suggestion of group.suggestions) {
      if (suggestion.isWildcard && suggestion.wildcardChildren.length === 1) {
        refs.add(suggestion.wildcardChildren[0].reference)
      }
    }
  }
  return refs
})

function resolveWildcardChildDiagnoses(wildcardSuggestion: Suggestion, children: GroupedWildcardChildItem[], isOwnOverrideBucket: boolean): SuggestionDiagnosis[] {
  const relevantChildren = isOwnOverrideBucket
    ? children
    : children.filter((child) => !overriddenWildcardChildRefs.value.has(child.reference))

  return relevantChildren
    .map((child) => wildcardSuggestion.diagnoses.find((diagnosis) => diagnosis.reference === child.reference))
    .filter((diagnosis): diagnosis is SuggestionDiagnosis => Boolean(diagnosis))
}

// One card per justification group returned by the grouped-by-justifications route — the backend
// already buckets suggestions sharing an identical justification (including wildcard-child
// overrides), so this only has to resolve each bucket's references into displayable diagnoses.
const justificationGroups = computed<JustificationGroup[]>(() => {
  if (props.isLoading || !groupedSuggestionJustifications.value) {
    return []
  }

  const groups: JustificationGroup[] = []
  for (const group of groupedSuggestionJustifications.value) {
    const dasSuggestions = _.filter(group.suggestions, { type: 'DAS' })
    if (!dasSuggestions.length) {
      continue
    }

    // A group justified only by chronicity (no textual/structured justification) has nothing to
    // show here — it's already rendered as its own card via chronicJustificationGroups below.
    // Without this guard it renders twice: once correctly as a chronic card, once here as an empty
    // "Pas de justification" card.
    if (group.chronicJustifications.length && !group.textualJustifications.length && !group.structuredJustifications.length) {
      continue
    }

    const diagnoses: CardDiagnosis[] = []
    const wildcards: { reference: string, level: Suggestion['level'] }[] = []
    for (const suggestion of dasSuggestions) {
      if (!suggestion.isWildcard) {
        const diagnosis = diagnosisByReference.value[suggestion.reference]
        if (diagnosis) {
          diagnoses.push(diagnosis)
        }
        continue
      }

      const wildcardSuggestion = wildcardSuggestionByReference.value[suggestion.reference]
      if (!wildcardSuggestion) {
        continue
      }

      const childDiagnoses = resolveWildcardChildDiagnoses(wildcardSuggestion, suggestion.wildcardChildren, suggestion.wildcardChildren.length === 1)
      if (!childDiagnoses.length) {
        continue
      }

      wildcards.push({ reference: wildcardSuggestion.reference, level: wildcardSuggestion.level })
      diagnoses.push(...childDiagnoses.map((diagnosis) => ({ ...diagnosis, wildcard: wildcardSuggestion.reference })))
    }
    if (!diagnoses.length) {
      continue
    }

    const staySavedSearches = _.uniqBy(_.flatMap(diagnoses, 'staySavedSearches'), 'search.id')
    groups.push({
      justification: {
        textualJustifications: group.textualJustifications,
        structuredJustifications: group.structuredJustifications,
        chronicJustifications: group.chronicJustifications,
        evaluations: [],
      },
      diagnoses,
      wildcards,
      staySavedSearches,
      searchNames: _.uniq(_.map(staySavedSearches, 'search.name')),
      searchIds: _.uniq(_.map(staySavedSearches, 'search.id')),
    })
  }
  return groups
})

// One card per backend-provided group carrying chronic justifications — same buckets as
// justificationGroups, filtered to the ones justified by a past stay rather than by text/search.
// Diagnosis metadata (level, description) is resolved via chronicDiagnosisByReference (unfiltered by
// show-more, unlike diagnosisByReference) since the suggestions endpoint now carries it for chronic
// DAS too.
const chronicJustificationGroups = computed<ChronicJustificationGroup[]>(() => {
  if (props.isLoading || !groupedSuggestionJustifications.value) {
    return []
  }

  const groups: ChronicJustificationGroup[] = []
  for (const group of groupedSuggestionJustifications.value) {
    if (!group.chronicJustifications.length) {
      continue
    }

    const dasSuggestions = _.filter(group.suggestions, { type: 'DAS' })
    const diagList = _.filter(_.map(dasSuggestions, (suggestion) => chronicDiagnosisByReference.value[suggestion.reference]))
    if (!diagList.length) {
      continue
    }

    groups.push({
      stayList: group.chronicJustifications.map((justification) => ({
        id: justification.sancareId,
        rssId: justification.rssId,
        stayEnd: justification.endDate,
        stayDuration: justification.stayDuration,
      })),
      diagList,
    })
  }
  return groups
})

onMounted(() => {
  store.dispatch('getGroupedSuggestionJustifications')
})

// The component isn't remounted when navigating from one stay to the next (same route, only its
// param changes — see StayDisplayerPage.vue's own watch(() => props.stayId, ...)), so without this
// watcher the grouped justifications would never be re-fetched after the first stay.
watch(() => store.state.stayDisplayer.originalStay?.id, () => {
  store.dispatch('getGroupedSuggestionJustifications')
})

watch(getGroupedSuggestionJustificationsRequest, (request) => {
  if (request.error) {
    store.dispatch('toast/setError', { message: 'Erreur lors du chargement des justifications groupées par diagnostic' })
  }
})

function switchDisplayMode() {
  emit('switch-display-mode')
}

function hasVisibleSuggestion() {
  return _.some(props.suggestedDiagnoses, (suggestion) => suggestion.isAlwaysVisible || showMore.value)
}

// Chronic DAS are suggested purely from the patient's history (backend's chronic_das CTE), so they
// can carry the flag on any diagnosis of any DAS suggestion, wildcard child included — flatten
// across all of them rather than assuming one suggestion per diagnosis. Used only for the header
// count and empty states: the cards themselves are rendered from chronicJustificationGroups above.
const chronicSuggestions = computed(() => _.filter(_.flatMap(props.suggestedDiagnoses, 'diagnoses'), 'isChronic'))

function showMoreDiags() {
  showMore.value = !showMore.value
}
</script>

<template>
  <div>
    <div class="card-body">
      <h1 class="card-title">
        {{ $t('diagnosis.'+type) }} <small class="fs-6">- {{ $t('stayDisplayer.sancareSuggestion', justificationGroups.length + chronicSuggestions.length) }}</small>
        <span
          v-tooltip="'Afficher en mode liste'"
          class="float-end"
          @click="switchDisplayMode()"
        >
          <sancare-octicon
            name="list-unordered"
            class="cp"
            :width="30"
            :height="30"
          />
        </span>
      </h1>
    </div>
    <div
      v-if="isLoading || getGroupedSuggestionJustificationsRequest.fetching"
      class="loader loader-lg loader-center"
    />
    <div
      v-else-if="!justificationGroups.length && !chronicSuggestions.length"
      class="card-body text"
    >
      Aucun diagnostic detecté
    </div>
    <div
      v-else-if="!hasVisibleSuggestion() && !chronicSuggestions.length"
      class="card-body text-center"
    >
      Aucun diagnostic pertinent
    </div>
    <suggested-diagnosis-card
      v-for="(justificationGroup, idx) in justificationGroups"
      :key="idx"
      :suggestion-group="justificationGroup"
      :rum-id="rumId"
      :health-data="healthData"
      :mode="mode"
      :readonly="readonly"
      :standard-mode-diagnosis-group="standardModeDiagnosisGroup"
      class="card justification-group mx-2 px-1 pb-1 mb-1"
      @make-selection="(suggestion) => $emit('make-selection', suggestion)"
    />
    <suggested-chronic-diagnosis-card
      v-for="(chronicJustificationGroup, idx) in chronicJustificationGroups"
      :key="`chronic-das-card-${idx}`"
      :chronic-justifications="chronicJustificationGroup"
      :mode="mode"
      :rum-id="rumId"
      :selection="selection"
      :readonly="readonly"
      :standard-mode-diagnosis-group="standardModeDiagnosisGroup"
      class="card justification-group mx-2 px-1 pb-1 mb-1"
      @make-selection="(suggestion) => $emit('make-selection', suggestion)"
    />
    <div class="col-12 my-1 py-0 text-center">
      <span
        class="btn-more-diagnosis"
        @click="showMoreDiags"
      >
        {{ showMore ? 'Afficher moins' : 'Afficher plus' }}
      </span>
    </div>
  </div>
</template>

<style lang="scss">
@use 'sass:color';

.card.justification-group {
  background-color: color.adjust(#22a2fe, $lightness: 10%);
}

.justification-group {
  border-color: white;
}

.card-title {
  font-size: 2rem;
}
</style>
